<template>
  <div class="payment-view">
    <Aguarde :show="httpSend"></Aguarde>

    <!-- Loading State -->
    <div v-if="isLoading" class="loading-state">
      <div class="loading-spinner"/>
      <p class="loading-text">Gerando QR Code...</p>
    </div>

    <template v-else>

      <!-- Header -->
      <div class="payment-header">
        <p class="payment-subtitle">pagamento via</p>
        <div class="pix-logo">
          <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
            <path d="M7 7L12.5 12.5L7 18" stroke="#32BCAD" stroke-width="2.5" stroke-linecap="round"
                  stroke-linejoin="round"/>
            <path d="M21 7L15.5 12.5L21 18" stroke="#32BCAD" stroke-width="2.5" stroke-linecap="round"
                  stroke-linejoin="round"/>
          </svg>
          <span>Pix</span>
        </div>
      </div>

      <!-- Card principal -->
      <div v-if="!paymentConfirmed" class="payment-card">

        <!-- QR Code -->
        <div class="qr-section">
          <p class="qr-label">Escaneie o código para pagar</p>

          <div class="qr-wrapper">
            <vue-qrcode v-if="qrCodeUrl" :margin="1" :value="qrCodeUrl"/>
            <div v-else class="qr-skeleton"/>
          </div>

          <!-- Timer -->
          <div class="timer-block" v-if="!paymentConfirmed">
            <div class="timer-ring">
              <svg width="44" height="44" viewBox="0 0 44 44" class="ring-svg">
                <circle cx="22" cy="22" r="18" fill="none" stroke="#e5e7eb" stroke-width="2.5"/>
                <circle
                    cx="22" cy="22" r="18"
                    fill="none"
                    stroke="#32BCAD"
                    stroke-width="2.5"
                    stroke-linecap="round"
                    :stroke-dasharray="circumference"
                    :stroke-dashoffset="strokeOffset"
                    class="ring-progress"
                />
              </svg>
              <span class="timer-number">{{ countdown }}</span>
            </div>

            <div class="timer-info">
              <p class="timer-meta">próxima verificação</p>
              <p class="timer-value">em {{ countdown === 1 ? '1 segundo' : `${countdown} segundos` }}</p>
            </div>

            <div class="status-indicator">
              <span class="status-dot" :class="statusClass"></span>
              <span class="status-text">{{ statusMessage }}</span>
            </div>
          </div>
        </div>

        <!-- Pix Copia e Cola -->
        <div class="copy-paste-section" v-if="pixCopyPasteCode">
          <p class="section-title">pix copia e cola</p>

          <div class="copy-wrapper">
            <span class="pix-code">{{ pixCopyPasteCode }}</span>
            <button class="copy-btn" :class="{ copied: isCopied }" @click="copyPixCode">
              <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                <template v-if="!isCopied">
                  <rect x="4" y="4" width="8" height="8" rx="1.5" stroke="currentColor" stroke-width="1.3"/>
                  <path d="M2 10V3a1 1 0 011-1h7" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>
                </template>
                <template v-else>
                  <circle cx="7" cy="7" r="6" stroke="currentColor" stroke-width="1.3"/>
                  <path d="M4.5 7l2 2 3-3" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"
                        stroke-linejoin="round"/>
                </template>
              </svg>
              {{ isCopied ? 'copiado!' : 'copiar' }}
            </button>
          </div>

          <Transition name="fade">
            <div class="copy-feedback" v-if="isCopied">
              <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                <circle cx="7" cy="7" r="6" stroke="#0F6E56" stroke-width="1.3"/>
                <path d="M4.5 7l2 2 3-3" stroke="#0F6E56" stroke-width="1.3" stroke-linecap="round"
                      stroke-linejoin="round"/>
              </svg>
              Código copiado! Cole no app do seu banco.
            </div>
          </Transition>
        </div>

        <div class="actions-section">
          <a @click="visualiza_boleto()" download class="action-btn btn btn-primary">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M7 1v8M4 6.5l3 3 3-3" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"
                    stroke-linejoin="round"/>
              <path d="M1 11h12" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>
            </svg>
            Baixar DUAM
          </a>
        </div>

        <!-- Divider -->
        <div class="divider"/>

        <!-- Instruções -->
        <div class="instructions-section">
          <p class="section-title">como pagar</p>

          <div class="steps-list">
            <div class="step-item" v-for="step in steps" :key="step.number" :class="{ 'step-last': step.type === 'deadline' }">
              <div class="step-badge" :class="`step-badge--${step.type}`">
                <svg v-if="step.type === 'deadline'" width="11" height="11" viewBox="0 0 12 12" fill="none">
                  <path d="M6 1v5l3 3" stroke="#92400E" stroke-width="1.5" stroke-linecap="round"/>
                  <circle cx="6" cy="6" r="5" stroke="#92400E" stroke-width="1.3"/>
                </svg>
                <span v-else>{{ step.number }}</span>
              </div>
              <p class="step-text" v-html="step.text"></p>
            </div>
          </div>
        </div>

      </div>

      <!-- Confirmação de pagamento -->
      <Transition v-if="paymentConfirmed" name="fade">
        <div class="payment-confirmed">
          <div class="confirmed-icon">
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
              <circle cx="16" cy="16" r="15" stroke="#0F6E56" stroke-width="1.5"/>
              <path d="M10 16.5l4.5 4.5 7.5-8" stroke="#0F6E56" stroke-width="2" stroke-linecap="round"
                    stroke-linejoin="round"/>
            </svg>
          </div>
          <p class="confirmed-title">Pagamento confirmado!</p>
          <p class="confirmed-sub">Obrigado. Seu pagamento foi recebido com sucesso.</p>
        </div>
      </Transition>

      <!-- Attempt label -->
      <p class="attempt-label" v-if="!paymentConfirmed">verificação #{{ attemptCount }}</p>

    </template>

  </div>
</template>

<script>
import VueQrcode from "vue-qrcode";
import {verificaPagamentoDUAM} from "@/api/financeiro/pagamento";
import {impressao} from "@/helps/impressao";
import {getFinanceiroGuia} from "@/api/relatorio/financeiro";
import Aguarde from "@/components/widgets/aguarde.vue";

export default {
  name: 'PixView',
  components: {Aguarde, VueQrcode},

  props: {
    orderId: {
      type: String,
      required: true,
    },
    dam_hash: {
      type: String,
      default: null,
    },
    qrCodeUrl: {
      type: String,
      default: null,
    },
    pixCopyPasteCode: {
      type: String,
      default: null,
    },
  },

  emits: ['payment-confirmed'],

  data() {
    return {
      httpSend:false,
      // Loading
      isLoading: true,

      // Timer
      countdown: 30,
      countdownInterval: null,
      circumference: 2 * Math.PI * 18,

      // Polling
      attemptCount: 0,

      // Status
      paymentConfirmed: false,
      statusMessage: 'aguardando',
      statusClass: 'pending',

      // Copia e cola
      isCopied: false,
      copyTimeout: null,

      // Passos
      steps: [
        {number: 1, type: 'default', text: 'Acesse o aplicativo da sua <strong>instituição financeira</strong>'},
        {
          number: 2,
          type: 'default',
          text: 'Dentro da opção <strong>"Pix"</strong>, toque em <strong>Pagar com QR Code</strong>'
        },
        {number: 3, type: 'default', text: 'Posicione o aparelho para a <strong>leitura do QR Code</strong>'},
        {number: 4, type: 'default', text: 'Confirme os dados e <strong>finalize o pagamento</strong>'},
        {number: 5, type: 'deadline', text: 'Realize o pagamento até a data de vencimento'},
      ],
    };
  },

  computed: {
    strokeOffset() {
      return this.circumference * (1 - this.countdown / 30);
    },
  },

  watch: {
    // Inicia o polling quando o orderId estiver disponível
    orderId: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.startPolling();
        }
      },
    },

    // Remove o loading quando os dados chegarem
    qrCodeUrl(newVal) {
      if (newVal) {
        this.isLoading = false;
      }
    },

    pixCopyPasteCode(newVal) {
      if (newVal) {
        this.isLoading = false;
      }
    },
  },

  mounted() {
    // Se as props já chegaram preenchidas, remove o loading imediatamente
    if (this.qrCodeUrl || this.pixCopyPasteCode) {
      this.isLoading = false;
    }
  },

  beforeUnmount() {
    this.clearIntervals();
  },

  methods: {
    visualiza_boleto() {
      this.httpSend = true;
      impressao(getFinanceiroGuia(this.dam_hash), 'P').catch(error => {
        console.log(error)
      }).finally(() => {
        this.httpSend = false;
      })
    },
    // ─── Polling ────────────────────────────────────────────
    startPolling() {
      this.clearIntervals();
      this.checkPaymentStatus();

      this.countdownInterval = setInterval(() => {
        this.countdown--;

        if (this.countdown <= 0) {
          this.countdown = 30;
          this.checkPaymentStatus();
        }
      }, 1000);
    },
    async checkPaymentStatus() {
      this.attemptCount++;
      this.statusMessage = 'verificando...';
      this.statusClass = 'checking';
      verificaPagamentoDUAM(this.dam_hash).then(result => {
        if (result.status) {
          if (result.dados) {
            this.handlePaymentConfirmed();
          } else {
            this.statusMessage = 'aguardando';
            this.statusClass = 'pending';
          }
        } else {
          this.statusMessage = 'erro na verificação';
          this.statusClass = 'error';
        }
      }).catch(error => {
        console.error('Erro ao verificar pagamento:', error);
        this.statusMessage = 'erro na verificação';
        this.statusClass = 'error';
      })
    },

    handlePaymentConfirmed() {
      this.clearIntervals();
      this.paymentConfirmed = true;
      this.$emit('payment-confirmed', this.orderId);
      setTimeout(() => {
        this.$router.push({name: "contribuinte-financeiro-guia"})
      }, 4000);
    },

    clearIntervals() {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    },

    // ─── Copia e Cola ───────────────────────────────────────

    async copyPixCode() {
      try {
        await navigator.clipboard.writeText(this.pixCopyPasteCode);
        this.isCopied = true;
        clearTimeout(this.copyTimeout);
        this.copyTimeout = setTimeout(() => {
          this.isCopied = false;
        }, 3000);
      } catch (err) {
        console.error('Erro ao copiar:', err);
      }
    },
  },
};
</script>

<style scoped>
/* ─── Layout ─────────────────────────────────────────────── */
.payment-view {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding-bottom: 1rem;
  max-width: 600px;
  margin: 0 auto;
}

/* ─── Loading ────────────────────────────────────────────── */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 3rem;
}

.loading-spinner {
  width: 36px;
  height: 36px;
  border: 2.5px solid #e5e7eb;
  border-top-color: #32BCAD;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.loading-text {
  margin: 0;
  font-size: 13px;
  color: #9ca3af;
}

/* ─── Header ─────────────────────────────────────────────── */
.payment-header {
  text-align: center;
}

.payment-subtitle {
  margin: 0 0 4px;
  font-size: 12px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #9ca3af;
  font-weight: 500;
}

.pix-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.pix-logo span {
  font-size: 22px;
  font-weight: 500;
  color: #111827;
  letter-spacing: -0.02em;
}

/* ─── Card ───────────────────────────────────────────────── */
.payment-card {
  width: 100%;
  background: #ffffff;
  border: 0.5px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
}

/* ─── QR Code ────────────────────────────────────────────── */
.qr-section {
  padding: 1.75rem;
  text-align: center;
  border-bottom: 0.5px solid #e5e7eb;
}

.qr-label {
  margin: 0 0 1.25rem;
  font-size: 13px;
  color: #6b7280;
}

.qr-wrapper {
  display: inline-block;
  background: white;
  padding: 14px;
  border-radius: 8px;
  border: 0.5px solid #e5e7eb;
  margin-bottom: 1.25rem;
}

.qr-wrapper img {
  width: 160px;
  height: 160px;
  display: block;
}

.qr-skeleton {
  width: 160px;
  height: 160px;
  border-radius: 8px;
  background: linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
}

/* ─── Timer ──────────────────────────────────────────────── */
.timer-block {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.timer-ring {
  position: relative;
  width: 44px;
  height: 44px;
  flex-shrink: 0;
}

.ring-svg {
  transform: rotate(-90deg);
}

.ring-progress {
  transition: stroke-dashoffset 1s linear;
}

.timer-number {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 13px;
  font-weight: 500;
  color: #111827;
}

.timer-info {
  text-align: left;
}

.timer-meta {
  margin: 0;
  font-size: 12px;
  color: #9ca3af;
}

.timer-value {
  margin: 0;
  font-size: 13px;
  font-weight: 500;
  color: #111827;
}

.status-indicator {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-left: 4px;
}

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}

.status-dot.pending {
  background: #32BCAD;
  animation: pulse 2s infinite;
}

.status-dot.checking {
  background: #f59e0b;
  animation: pulse 0.8s infinite;
}

.status-dot.error {
  background: #ef4444;
}

.status-text {
  font-size: 11px;
  color: #9ca3af;
}

/* ─── Copia e Cola ───────────────────────────────────────── */
.copy-paste-section {
  padding: 1.25rem 1.75rem;
  border-bottom: 0.5px solid #e5e7eb;
}

.section-title {
  margin: 0 0 10px;
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #9ca3af;
  font-weight: 500;
}

.copy-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f9fafb;
  border: 0.5px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px 12px;
}

.pix-code {
  flex: 1;
  font-size: 11px;
  font-family: monospace;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.copy-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  background: #32BCAD;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 7px 14px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s;
  white-space: nowrap;
}

.copy-btn:hover {
  background: #26a99b;
}

.copy-btn.copied {
  background: #0F6E56;
}

.copy-feedback {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  padding: 6px 10px;
  background: #E1F5EE;
  border-radius: 8px;
  font-size: 12px;
  color: #0F6E56;
  font-weight: 500;
}

/* ─── Instruções ─────────────────────────────────────────── */
.instructions-section {
  padding: 1.5rem 1.75rem;
}

.steps-list {
  display: flex;
  flex-direction: column;
}

.step-item {
  display: flex;
  gap: 14px;
  align-items: flex-start;
  padding: 10px 0;
  border-bottom: 0.5px solid #f3f4f6;
}

.step-item.step-last {
  border-bottom: none;
}

.step-badge {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 1px;
  font-size: 11px;
  font-weight: 500;
}

.step-badge--default {
  background: #E1F5EE;
  color: #0F6E56;
}

.step-badge--deadline {
  background: #FEF3C7;
}


/* ─── Ações ──────────────────────────────────────────────── */
.actions-section {
  display: flex;
  gap: 10px;
  padding: 1.25rem 1.75rem;
  border-bottom: 0.5px solid #e5e7eb;
}

.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  flex: 1;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;
  transition: background 0.15s, border-color 0.15s;
}

.action-btn--outline {
  background: transparent;
  border: 0.5px solid #d1d5db;
  color: #374151;
}

.action-btn--outline:hover {
  background: #f9fafb;
  border-color: #9ca3af;
}

.action-btn--solid {
  background: #1a1a2e;
  border: 0.5px solid #1a1a2e;
  color: white;
}

.action-btn--solid:hover {
  background: #2d2d4e;
  border-color: #2d2d4e;
}

/* ─── Divider ────────────────────────────────────────────── */
.divider {
  height: 0.5px;
  background: #e5e7eb;
}

.step-text {
  margin: 0;
  font-size: 13px;
  color: #111827;
  line-height: 1.5;
}

.step-text :deep(strong) {
  font-weight: 500;
}

.step-text :deep(.deadline-date) {
  font-weight: 500;
  color: #92400E;
}

/* ─── Confirmação ────────────────────────────────────────── */
.payment-confirmed {
  width: 100%;
  background: #ffffff;
  border: 0.5px solid #e5e7eb;
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
}

.confirmed-icon {
  margin-bottom: 1rem;
}

.confirmed-title {
  margin: 0 0 6px;
  font-size: 17px;
  font-weight: 500;
  color: #0F6E56;
}

.confirmed-sub {
  margin: 0;
  font-size: 13px;
  color: #6b7280;
}

/* ─── Attempt label ──────────────────────────────────────── */
.attempt-label {
  margin: 0;
  font-size: 11px;
  color: #9ca3af;
}

/* ─── Animações ──────────────────────────────────────────── */
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.3;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>